import { AxiosError } from 'axios';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { BehaviorSubject, merge, of, Subject, Subscription, throwError, timer } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import {
  catchError,
  delay,
  exhaustMap,
  map,
  switchMap,
  take,
  takeWhile,
  tap
} from 'rxjs/operators';
import {
  GetList,
  ListActions,
  ListError,
  ListResponse,
  ListResponseData,
  RefreshList,
  ResetList
} from '../../list-conductor/list-actions';
import { ListConductor } from '../../list-conductor/list-conductor';
import { ListMetaNetworkParams } from '../../list-conductor/list-meta-data';
import { ListConductorOptions } from '../../list-conductor/options';
import { useListConductorNavigationProvider } from '../../list-conductor/useListConductorNavigationProvider';
import { useDontMountAtFirst } from '../../shared/hooks/useDontMountAtFirst';
import { BackendSubscriptionQuery } from '../interfaces/backend-subscription';
import { EntityUIState } from '../interfaces/entity-ui-state';
import { backendSubscriptionService } from '../services/backend-subscription.service';

export interface UseQueryProps<P, D, L, Pg extends boolean> {
  dataSource$: (params?: P | ListMetaNetworkParams) => Promise<QueryResult<D>>;
  pollDataSource$?: (data?: D, params?: P | ListMetaNetworkParams) => Promise<D>;
  pollDataMergeFn?: (oldData: D, newData: D) => D;
  pollInterval?: number;
  pollUntil?: (data: QueryResult<D>) => boolean;
  terminatePollOnError?: boolean;
  pollDelay?: number;
  noPollingOnEmptyData?: boolean;
  emptyAssertion?: (data: QueryResult<D>) => boolean;
  fetchSuccess?: (data: QueryResult<D>) => void;
  fetchError?: (error: AxiosError) => void;
  paginated?: Pg;
  listConductorOptions?: ListConductorOptions<L>;
  resetOnFetch?: boolean;
  initialData?: D;
  initialDelay?: number;
  subscriptionQueries?: BackendSubscriptionQuery<D>[];
}

export type QueryDataUpdateFunction<D> = (data?: D) => D;
export interface PaginatedResponse<D> {
  items: D;
  responseMetaData: ListResponseData;
}

export type QueryResult<D> = PaginatedResponse<D> | D;

type FetchFunction<P> = (params?: P) => void;

const INITIAL_STATE = {
  uiState: EntityUIState.INIT
};

interface QueryState<D> {
  uiState: EntityUIState;
  error?: AxiosError;
  data?: D;
}

enum QueryActionKind {
  WRITE_DATA = 'WRITE_DATA',
  RESET = 'RESET',
  SET_DATA = 'SET_DATA'
}

interface WriteDataAction<D> {
  type: QueryActionKind.WRITE_DATA;
  data: QueryDataUpdateFunction<D>;
}

interface ResetAction {
  type: QueryActionKind.RESET;
}

interface SetDataAction<D> {
  type: QueryActionKind.SET_DATA;
  data: Partial<QueryState<D>>;
}

type QueryAction<D> = WriteDataAction<D> | ResetAction | SetDataAction<D>;

function stateReducer<D>(state: QueryState<D>, action: QueryAction<D>): QueryState<D> {
  const { type } = action;

  switch (type) {
    case QueryActionKind.RESET:
      return {
        ...INITIAL_STATE
      };
    case QueryActionKind.SET_DATA:
      return {
        ...state,
        ...action.data
      };
    case QueryActionKind.WRITE_DATA:
      return {
        ...state,
        data: action.data(state.data)
      };
    default:
      return state;
  }
}

export function useQuery<P, D, L, Pg extends boolean>(props: UseQueryProps<P, D, L, Pg>) {
  const {
    dataSource$,
    pollDataSource$,
    pollDataMergeFn,
    pollInterval,
    pollUntil,
    pollDelay,
    noPollingOnEmptyData = false,
    emptyAssertion,
    fetchSuccess,
    fetchError,
    paginated,
    listConductorOptions,
    resetOnFetch = true,
    initialData,
    initialDelay = 0,
    terminatePollOnError,
    subscriptionQueries
  } = props;

  const listConductorNavigationProvider = useListConductorNavigationProvider();

  const initialState: QueryState<D> = {
    ...INITIAL_STATE
  };

  if (initialData) {
    initialState.data = initialData;
    initialState.uiState = EntityUIState.IDLE;
  }

  const [state, dispatch] = useReducer(stateReducer<D>, { ...initialState });

  const fetchEffectSub = useRef(Subscription.EMPTY);
  const backgroundFetchEffectSub = useRef(Subscription.EMPTY);
  const refetchSubject = useRef(new Subject());
  const fetchedOnce = useRef(false);
  const pollConfigSubject = useRef(
    new BehaviorSubject({
      delay: pollDelay || pollInterval,
      interval: pollInterval
    })
  );

  const dataRef = useRef<D>();
  dataRef.current = state.data;

  const _doFetch = useRef<FetchFunction<P>>(() => {});

  const listConductorRef = useRef<ListConductor<L> | undefined>(
    paginated
      ? new ListConductor(
          listConductorOptions ? { ...listConductorOptions } : {},
          listConductorNavigationProvider
        )
      : undefined
  );

  const _getBackgroundFetchTriggers = (data: QueryResult<D>) => {
    const backgroundFetchTriggers = [refetchSubject.current.asObservable().pipe(delay(0))];

    if (!pollInterval || (noPollingOnEmptyData && !data)) {
      return backgroundFetchTriggers;
    }

    if (typeof pollUntil === 'function' && !pollUntil(data)) {
      return backgroundFetchTriggers;
    }

    backgroundFetchTriggers.push(
      pollConfigSubject.current.pipe(
        switchMap(pollConfig => {
          return timer(pollConfig.delay as number, pollConfig.interval as number);
        })
      )
    );

    return backgroundFetchTriggers;
  };

  _doFetch.current = useCallback(
    (params?: P) => {
      fetchEffectSub.current.unsubscribe();
      backgroundFetchEffectSub.current.unsubscribe();

      const fetchEffect = () => {
        return fromPromise(
          dataSource$(
            paginated && listConductorRef.current
              ? listConductorRef.current.getListMetaNetworkParams()
              : params
          )
        );
      };

      fetchEffectSub.current = fetchEffect()
        .pipe(
          delay(initialDelay),
          map((data: QueryResult<D>) => {
            if (paginated && listConductorRef.current) {
              const paginatedResponse = data as PaginatedResponse<D>;

              dispatch({
                type: QueryActionKind.SET_DATA,
                data: {
                  data: paginatedResponse.items
                }
              });

              listConductorRef.current.dispatch(
                new ListResponse(paginatedResponse.responseMetaData)
              );

              return paginatedResponse;
            }

            dispatch({
              type: QueryActionKind.SET_DATA,
              data: {
                uiState:
                  emptyAssertion && emptyAssertion(data) ? EntityUIState.EMPTY : EntityUIState.IDLE,
                error: undefined,
                data: data as D
              }
            });

            return data;
          }),
          tap((data: QueryResult<D>) => {
            backgroundFetchEffectSub.current = merge(..._getBackgroundFetchTriggers(data))
              .pipe(
                exhaustMap(() => {
                  const pollEffect = pollDataSource$
                    ? fromPromise(
                        pollDataSource$(
                          dataRef.current,
                          paginated && listConductorRef.current
                            ? listConductorRef.current.getListMetaNetworkParams()
                            : params
                        )
                      )
                    : fetchEffect();

                  return pollEffect.pipe(
                    map(pollResult => {
                      const newData = pollDataMergeFn
                        ? pollDataMergeFn(dataRef.current!, pollResult as D)
                        : pollResult;

                      if (paginated && listConductorRef.current) {
                        const paginatedResponse = data as PaginatedResponse<D>;

                        listConductorRef.current.dispatch(
                          new ListResponse(paginatedResponse.responseMetaData)
                        );

                        dispatch({
                          type: QueryActionKind.SET_DATA,
                          data: {
                            data: newData as D
                          }
                        });
                      } else {
                        dispatch({
                          type: QueryActionKind.SET_DATA,
                          data: {
                            uiState:
                              emptyAssertion && emptyAssertion(newData)
                                ? EntityUIState.EMPTY
                                : EntityUIState.IDLE,
                            data: newData as D
                          }
                        });
                      }

                      if (typeof fetchSuccess === 'function') {
                        fetchSuccess(newData);
                      }

                      return newData;
                    }),
                    catchError(error => {
                      if (!terminatePollOnError) {
                        return of();
                      }

                      throw error;
                    })
                  );
                }),
                catchError(error => {
                  if (typeof fetchError === 'function') {
                    fetchError(error);
                  }

                  dispatch({
                    type: QueryActionKind.SET_DATA,
                    data: {
                      uiState: EntityUIState.ERRORED,
                      error,
                      data: undefined
                    }
                  });

                  throw error;
                }),
                takeWhile(val =>
                  typeof pollUntil === 'function' ? pollUntil(val as QueryResult<D>) : true
                )
              )
              .subscribe({
                error: () => {}
              });
          }),
          catchError(error => {
            if (typeof fetchError === 'function') {
              fetchError(error);
            }

            if (paginated && listConductorRef.current) {
              listConductorRef.current.dispatch(new ListError(error));
              dispatch({
                type: QueryActionKind.SET_DATA,
                data: {
                  data: undefined
                }
              });

              return throwError(error);
            }

            dispatch({
              type: QueryActionKind.SET_DATA,
              data: {
                uiState: EntityUIState.ERRORED,
                error,
                data: undefined
              }
            });

            return throwError(error);
          }),
          take(1),
          tap((data: QueryResult<D>) => {
            if (typeof fetchSuccess === 'function') {
              fetchSuccess(data);
            }

            fetchedOnce.current = true;
          })
        )
        .subscribe({
          error: () => {}
        });
    },
    [props]
  );

  const fetch = (params?: P) => {
    if (fetchedOnce.current && !resetOnFetch) {
      refetch();
      return;
    }

    fetchedOnce.current = false;

    if (paginated && listConductorRef.current) {
      listConductorRef.current.dispatch(new GetList());
      return;
    }

    dispatch({
      type: QueryActionKind.SET_DATA,
      data: {
        uiState: EntityUIState.LOADING,
        error: undefined,
        data: undefined
      }
    });

    _doFetch.current(params);
  };

  const refetch = () => {
    if (paginated && listConductorRef.current) {
      listConductorRef.current.dispatch(new RefreshList());
      return;
    }

    dispatch({
      type: QueryActionKind.SET_DATA,
      data: {
        uiState: EntityUIState.REFRESHING,
        error: undefined
      }
    });

    refetchSubject.current.next();
  };

  const reset = () => {
    fetchedOnce.current = false;

    if (paginated && listConductorRef.current) {
      listConductorRef.current.dispatch(new ResetList());
      return;
    }

    dispatch({
      type: QueryActionKind.RESET
    });
  };

  const writeData = (updateFunction: QueryDataUpdateFunction<D>) => {
    dispatch({
      type: QueryActionKind.WRITE_DATA,
      data: updateFunction
    });
  };

  const restartPolling = (newDelay = 0, newPollInterval = pollInterval) => {
    pollConfigSubject.current.next({
      delay: newDelay,
      interval: newPollInterval
    });
  };

  useEffect(() => {
    if (!listConductorRef.current) {
      return () => {};
    }

    const listStateSub = listConductorRef.current.listState$.subscribe(listState => {
      dispatch({
        type: QueryActionKind.SET_DATA,
        data: {
          uiState: listState.state,
          error: listState.error
        }
      });
    });

    const listChangesSub = listConductorRef.current.changes$.subscribe(() => {
      if (listConductorRef.current?.getListState().state === EntityUIState.REFRESHING) {
        refetchSubject.current.next();
      } else {
        _doFetch.current();
      }
    });

    return () => {
      listStateSub.unsubscribe();
      listChangesSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    return () => {
      fetchEffectSub.current.unsubscribe();
      backgroundFetchEffectSub.current.unsubscribe();

      if (listConductorRef.current) {
        listConductorRef.current.disconnect();
      }
    };
  }, []);

  useDontMountAtFirst(() => {
    pollConfigSubject.current.next({
      delay: pollDelay || 0,
      interval: pollInterval
    });
  }, [pollInterval, pollDelay]);

  useEffect(() => {
    if (!subscriptionQueries?.length) {
      return () => {};
    }

    const sub = backendSubscriptionService.events$.subscribe(event => {
      const matchedQuery = subscriptionQueries.find(subscription => {
        let isMatch = true;

        if (subscription.event !== event.ev) {
          isMatch = false;
        }

        const areSubscriptionsIdsSubsetOfEventIds = (event.ids || [])
          .join(',')
          .includes((subscription.ids || []).join(','));

        if (!areSubscriptionsIdsSubsetOfEventIds) {
          isMatch = false;
        }

        return isMatch;
      });

      if (!matchedQuery) {
        return;
      }

      refetch();
    });

    return () => {
      sub.unsubscribe();
    };
  }, []);

  const dispatchListAction = (action: ListActions) => {
    if (!listConductorRef.current) {
      return;
    }

    listConductorRef.current.dispatch(action);
  };

  const shouldDisableFilters =
    state.uiState === EntityUIState.ERRORED || state.uiState === EntityUIState.NEW;

  return {
    uiState: state.uiState,
    error: state.error,
    data: state.data,
    shouldDisableFilters,
    listConductor: listConductorRef.current,
    refetchSubject,
    fetch,
    refetch,
    reset,
    restartPolling,
    writeData,
    dispatchListAction
  };
}
